import { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Plus, Trash2, Edit, AlertCircle, Loader2, DatabaseZap } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Checkbox,
  DataTable,
  Pagination,
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  SearchInput,
  toast,
  useAuth,
  useLocale,
} from '@aaas/shared';
import { useUrlPagination } from '../../hooks/useUrlPagination';
import type { DataTableColumn, DataTableAction } from '@aaas/shared';
import { getDefaultEventTypes } from '../../constants/referenceDefaults';
import { useCalendarEventTypeListQuery } from '../../hooks/useCalendarEventTypeQueries';
import calendarEventTypeService from '../../services/calendar-event-type.service';
import { CalendarEventTypeFormDialog } from '../../components/CalendarEventTypeFormDialog';
import { useATSRole } from '../../hooks/useATSRole';
import type { CalendarEventType } from '../../types/calendar-event-type';

export function EventTypeConfigPage() {
  const { t } = useTranslation('ats');
  const { user, idToken, currentWorkspace } = useAuth();
  const { isLeaderOrAbove } = useATSRole();
  const { locale } = useLocale();
  const defaultEventTypes = useMemo(() => getDefaultEventTypes(locale), [locale]);
  const queryClient = useQueryClient();
  const invalidateEventTypes = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['calendar-event-types'] });
  }, [queryClient]);
  const { page, size, sortBy, order, filters, setPage, setSize, setSort, setFilters } =
    useUrlPagination({ filterKeys: ['search'] });
  const searchValue = filters.search as string | undefined;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState<CalendarEventType | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingEventType, setEditingEventType] = useState<CalendarEventType | null>(null);
  const [showDefaultDialog, setShowDefaultDialog] = useState(false);
  const [isCreatingDefaults, setIsCreatingDefaults] = useState(false);
  const [selectedDefaults, setSelectedDefaults] = useState<Set<string>>(
    () => new Set(defaultEventTypes),
  );

  useEffect(() => {
    setSelectedDefaults(new Set(defaultEventTypes));
  }, [defaultEventTypes]);

  // React Query for CalendarEventType list
  const {
    data: eventTypeResponse,
    isLoading,
    isFetching,
    error,
  } = useCalendarEventTypeListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    page,
    size,
    sortBy,
    order,
    search: searchValue,
    enabled: !!user && !!currentWorkspace,
  });

  // Extract data from response
  const eventTypes = eventTypeResponse?.data?.items || [];
  const totalItems = eventTypeResponse?.data?.total || 0;
  const totalPages = Math.ceil(totalItems / size);

  const handleCreateClick = useCallback(() => {
    setFormMode('create');
    setEditingEventType(null);
    setShowFormDialog(true);
  }, []);

  const handleEditClick = useCallback((record: CalendarEventType) => {
    setFormMode('edit');
    setEditingEventType(record);
    setShowFormDialog(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setShowFormDialog(false);
    setEditingEventType(null);
  }, []);

  const handleFormSuccess = useCallback(() => {
    invalidateEventTypes();
  }, [invalidateEventTypes]);

  const handleDeleteClick = useCallback((record: CalendarEventType) => {
    setSelectedEventType(record);
    setDeleteError(null);
    setShowDeleteDialog(true);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!selectedEventType || !user) return;

    setIsDeleting(true);
    setDeleteError(null);
    setDeletingIds((prev) => new Set(prev).add(selectedEventType.id));

    try {
      const token = await user.getIdToken();
      await calendarEventTypeService.deleteCalendarEventType(currentWorkspace!.id, selectedEventType.id, token);
      invalidateEventTypes();
      setShowDeleteDialog(false);
      setSelectedEventType(null);
    } catch (err: unknown) {
      let errorMessage = t('config.unknownError');
      if (axios.isAxiosError(err) && err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setDeleteError(errorMessage);
    } finally {
      setIsDeleting(false);
      setDeletingIds((prev) => {
        const newSet = new Set(prev);
        if (selectedEventType) {
          newSet.delete(selectedEventType.id);
        }
        return newSet;
      });
    }
  };

  const handleDialogClose = () => {
    if (!isDeleting) {
      setShowDeleteDialog(false);
      setSelectedEventType(null);
      setDeleteError(null);
    }
  };

  const handleCreateDefaults = async () => {
    if (!user || !currentWorkspace || selectedDefaults.size === 0) return;
    setIsCreatingDefaults(true);
    try {
      const token = await user.getIdToken();
      const names = defaultEventTypes.filter((n) => selectedDefaults.has(n));
      await calendarEventTypeService.bulkCreateCalendarEventTypes(
        currentWorkspace.id,
        token,
        names,
      );
      invalidateEventTypes();
      setShowDefaultDialog(false);
    } catch (err: unknown) {
      const message = axios.isAxiosError(err) && err.response?.data?.detail
        ? err.response.data.detail
        : t('eventType.createDefaultsFailed');
      toast.error(message);
    } finally {
      setIsCreatingDefaults(false);
    }
  };

  // Define table columns
  const columns: DataTableColumn<CalendarEventType>[] = [
    {
      key: 'name',
      title: t('eventType.columnName'),
      sortable: true,
      render: (value: string) => <span className="font-medium text-gray-900">{value}</span>,
    },
    {
      key: 'created_at',
      title: t('config.columnCreated'),
      width: 120,
      sortable: true,
      render: (value: string) => new Date(value).toLocaleDateString(),
    },
  ];

  // Define table actions (only for leader+)
  const actions: DataTableAction<CalendarEventType>[] = isLeaderOrAbove
    ? [
        {
          key: 'edit',
          label: t('config.edit'),
          icon: <Edit className="h-4 w-4" />,
          onClick: handleEditClick,
          inline: true,
        },
        {
          key: 'delete',
          label: t('config.delete'),
          icon: <Trash2 className="h-4 w-4" />,
          loadingIcon: <Loader2 className="h-4 w-4 animate-spin" />,
          onClick: handleDeleteClick,
          loading: (record) => deletingIds.has(record.id),
          destructive: true,
          inline: true,
        },
      ]
    : [];

  if (isLoading && !eventTypeResponse) {
    return (
      <div className="w-full p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CalendarClock className="h-8 w-8 text-blue-600" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">{t('eventType.pageTitle')}</h1>
              {isFetching && !isLoading && (
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              )}
            </div>
            <p className="text-sm text-gray-500">{t('eventType.pageDescription')}</p>
          </div>
        </div>
        {isLeaderOrAbove && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setSelectedDefaults(new Set(defaultEventTypes));
                setShowDefaultDialog(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <DatabaseZap className="h-4 w-4" />
              {t('config.createDefaults')}
            </button>
            <button
              onClick={handleCreateClick}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              {t('eventType.newOne')}
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <SearchInput
        placeholder={t('eventType.searchPlaceholder')}
        onSearch={(value) => setFilters({ ...filters, search: value || undefined })}
        initialValue={searchValue || ''}
      />

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : t('eventType.loadFailed')}
          </AlertDescription>
        </Alert>
      )}

      {/* Data Table */}
      <div className="space-y-4">
        <DataTable
          columns={columns}
          data={eventTypes}
          loading={isLoading && !eventTypeResponse}
          actions={actions}
          rowKey={(record) => record.id}
          emptyMessage={t('eventType.empty')}
          onSort={setSort}
          sortKey={sortBy}
          sortDirection={order}
        />

        {/* Pagination */}
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          pageSize={size}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setSize}
          showTotal={true}
          showPageSizeSelector={true}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={handleDialogClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('eventType.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('eventType.deleteBody')}
            </AlertDialogDescription>
            {deleteError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                <span className="text-sm text-red-600">{deleteError}</span>
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t('config.cancel')}</AlertDialogCancel>
            <Button
              onClick={(e) => {
                e.preventDefault();
                handleDeleteConfirm();
              }}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('config.deleting')}
                </>
              ) : (
                t('config.delete')
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create/Edit EventType Dialog */}
      <CalendarEventTypeFormDialog
        open={showFormDialog}
        onClose={handleFormClose}
        mode={formMode}
        calendarEventType={editingEventType}
        onSuccess={handleFormSuccess}
      />

      {/* Create Defaults Dialog */}
      <AlertDialog open={showDefaultDialog} onOpenChange={(open) => !isCreatingDefaults && setShowDefaultDialog(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('eventType.defaultsTitle')}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>{t('config.createDefaultsSelect')}</p>
                <div className="mt-3 space-y-2 max-h-60 overflow-y-auto">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={selectedDefaults.size === defaultEventTypes.length}
                      onCheckedChange={(checked) => {
                        setSelectedDefaults(checked ? new Set(defaultEventTypes) : new Set());
                      }}
                    />
                    <span className="text-sm font-medium">{t('config.createDefaultsSelectAll')}</span>
                  </label>
                  <hr className="my-1" />
                  {defaultEventTypes.map((name) => (
                    <label key={name} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={selectedDefaults.has(name)}
                        onCheckedChange={(checked) => {
                          setSelectedDefaults((prev) => {
                            const next = new Set(prev);
                            if (checked) next.add(name);
                            else next.delete(name);
                            return next;
                          });
                        }}
                      />
                      <span className="text-sm">{name}</span>
                    </label>
                  ))}
                </div>
                <p className="mt-3 text-sm">{t('config.createDefaultsSkipNote')}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCreatingDefaults}>{t('config.cancel')}</AlertDialogCancel>
            <Button
              onClick={(e) => {
                e.preventDefault();
                handleCreateDefaults();
              }}
              disabled={isCreatingDefaults || selectedDefaults.size === 0}
            >
              {isCreatingDefaults ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('config.creating')}
                </>
              ) : (
                t('config.confirm')
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
